import React, { memo, useCallback, useEffect, useState } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import _memoize from 'lodash/memoize';
import { hooks, media } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as VideoIcon } from '@lifesize/ux-assets/vectors/svgIcons/video.svg';
import { ReactComponent as VideoOffIcon } from '@lifesize/ux-assets/vectors/svgIcons/video-off.svg';
import { Fab, makeStyles, Tooltip } from '@material-ui/core';
import { MUTE_VIDEO } from 'constants/lsqaConstants';
import { setPillNotificationWithTimeout } from 'actions/Notification';
import { LONG_TIMEOUT } from 'constants/Notification';
import { setVideoMute } from '../../actions/media';

interface Props {
  intl: { formatMessage: Function };
  isPreCall?: boolean;
  useLiteButton?: boolean;
  useLightTheme?: boolean;
}

const messages = defineMessages({
  cameraOn: {
    id: 'components.incall.controls.videoMute.tooltip.on',
    defaultMessage: 'Turn camera on'
  },
  cameraOff: {
    id: 'components.incall.controls.videoMute.tooltip.off',
    defaultMessage: 'Turn camera off'
  },
  noCameraDetected: {
    id: 'components.avSettingsModal.noCamera',
    defaultMessage: 'No camera detected'
  },
  deviceNotAllowed: {
    id: 'components.incall.controls.videoButton.tooltip.unavailable',
    defaultMessage: 'No camera detected. Please allow access to your device in your browser or system settings.'
  }
});

const VideoButton = (props: Props) => {
  const { intl, isPreCall = false, useLightTheme, useLiteButton } = props;
  const dispatch = useDispatch();
  const { localVideoMuted: isMuted } = hooks.useMedia();
  const videoDevice = hooks.useVideoDevice();
  const hasVideoDevice = !!videoDevice?.deviceId;
  const isConnected = hooks.useCallConnected();
  const isAcquiring = media.isAcquiring();
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const [label, setLabel] = useState<NonNullable<React.ReactNode>>(<></>);
  const classes = useStyles({ ...getStyleOverride(isMuted, useLightTheme, useLiteButton) });
  const tooltipClasses = useStylesTooltip();

  const handleClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setTooltipOpen(true);
  }, []);

  const getDisabled = useCallback(() => {
    if (useLiteButton) {
      return !isAcquiring && !hasVideoDevice;
    } else {
      return !hasVideoDevice || (!isConnected && !isPreCall);
    }
  }, [isAcquiring, hasVideoDevice, isConnected, useLiteButton, isPreCall]);

  const isDisabled = getDisabled();

  const getTooltipMessage = useCallback(
    (isMuted: boolean, isDisabled?: boolean): NonNullable<React.ReactNode> => {
      if (isDisabled) {
        return intl.formatMessage(messages.noCameraDetected);
      }
      if (isMuted) {
        return intl.formatMessage(messages.cameraOn);
      }
      return intl.formatMessage(messages.cameraOff);
    },
    [intl]
  );

  /** This prevents the text from changing while the tooltip is open */
  useEffect(() => {
    if (!isTooltipOpen) {
      setLabel(getTooltipMessage(isMuted, isDisabled));
    }
  }, [isMuted, isDisabled, isTooltipOpen, getTooltipMessage]);

  const handleClick = useCallback(
    async (isVideoMuted) => {
      // @ts-ignore
      dispatch(setVideoMute(!isVideoMuted));
    },
    [dispatch]
  );

  /**
   * Solely to display reminder pill that the camera is disabled
   */
  const handleSpanClick = useCallback(() => {
    if (isConnected && videoDevice && !videoDevice?.deviceId) {
      // Make sure tooltip is closed before displaying pill
      setTooltipOpen(false);
      dispatch(setPillNotificationWithTimeout(intl.formatMessage(messages.deviceNotAllowed), LONG_TIMEOUT));
    }
  }, [dispatch, intl, isConnected, videoDevice]);

  return (
    <Tooltip
      title={label}
      placement="top"
      arrow={true}
      classes={tooltipClasses}
      onOpen={handleOpen}
      onClose={handleClose}
      open={isTooltipOpen}
    >
      <span data-testid={isMuted ? 'unmute-video-btn' : 'mute-video-btn'} onClick={handleSpanClick}>
        <Fab
          classes={classes}
          disabled={isDisabled}
          disableFocusRipple={true}
          onClick={() => handleClick(isMuted)}
          size="medium"
          {...MUTE_VIDEO}
        >
          {isMuted || isDisabled ? <VideoOffIcon data-testid="videoOffIcon" /> : <VideoIcon data-testid="videoIcon" />}
        </Fab>
      </span>
    </Tooltip>
  );
};

/** Style selectors that can be dynamically modified */
interface StyleProps {
  borderColor: string;
  color: string;
  disabledBackground: string;
  opacity: number;
}

const getStyleOverride = _memoize(
  (isMuted: boolean, useLightTheme?: boolean, useLiteButton?: boolean): StyleProps => {
    return {
      borderColor: isMuted ? colors.alertRed : useLightTheme ? colors.squidInk : colors.white,
      color: isMuted ? colors.alertRed : useLightTheme ? colors.squidInk : colors.white,
      opacity: isMuted ? 0.8 : useLightTheme ? 1.0 : 0.6,
      disabledBackground: useLiteButton ? 'transparent !important' : `${colors.squidInk} !important`
    };
  },
  (isMuted, useLightTheme, useLiteButton) => `${isMuted}${useLightTheme}${useLiteButton}`
);

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: 'transparent',
    border: (btnStyle: StyleProps) => `2px solid ${btnStyle.borderColor}`,
    boxShadow: 'none',
    color: (btnStyle: StyleProps) => btnStyle.color,
    margin: 0,
    opacity: (btnStyle: StyleProps) => btnStyle.opacity,
    '&:hover': {
      backgroundColor: 'transparent',
      borderColor: (btnStyle: StyleProps) => btnStyle.borderColor,
      color: (btnStyle: StyleProps) => btnStyle.color,
      opacity: 1
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  },
  disabled: {
    color: `${colors.sentinel} !important`,
    borderColor: `${colors.sentinel} !important`,
    backgroundColor: (btnStyle: StyleProps) => btnStyle.disabledBackground,
    opacity: `1 !important`
  }
}));

const useStylesTooltip = makeStyles(() => ({
  popper: {
    textAlign: 'center',
    zIndex: 999999999 // This is so tooltip is not hidden by z-index of call preview
  }
}));

export default memo(injectIntl(VideoButton));
